import "./style.scss";

import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col } from "@amzn/stencil-react-components/layout";
import { ModalContent } from "@amzn/stencil-react-components/modal";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Label, Text } from "@amzn/stencil-react-components/text";
import Button from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import TextArea from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { License } from "common/components/LicenseSelect/LicenseSelect";
import { CreateOrderRequestContent } from "common/types";
import { MessageStruct } from "common/types/ui-types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { UserPermissions } from "lib/services";
import { ItemTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import _ from "lodash";
import React, { FunctionComponent, useEffect, useState } from "react";

import { AuditTemp } from "../..";
import { AcknowledgmentInfo, AcknowledgmentsList } from "../Acknowledgments";
import { MessagesList } from "../MessagesList";

export interface SubmitUnsanctionedOrderModalProps {
  taxonomyId: string;
  submitOrderHandler: (request: CreateOrderRequestContent) => void;
  close: () => void;
  csrfTokenIsLoading: boolean;
  isLoadingCreate: boolean;
  messages?: MessageStruct[];
  externalButtonText?: string;
  orderWorkflow: string;
  licenseInfo?: License;
}

/**
 * Reusable component to submit a unsanctioned order
 */

export const SubmitUnsanctionedOrderModal: FunctionComponent<SubmitUnsanctionedOrderModalProps> = ({
  submitOrderHandler,
  close,
  csrfTokenIsLoading,
  isLoadingCreate,
  messages,
  taxonomyId,
  externalButtonText,
  orderWorkflow,
  licenseInfo,
}: SubmitUnsanctionedOrderModalProps) => {
  const acknowledgePolicyName = getString("submitUnsanctionOrder.policy.name");
  const userAlias = UserPermissions.current.username;
  const [confirmIsChecked, setConfirmIsChecked] = useState(false);
  const [securityTicket, setSecurityTicket] = useState<undefined | string>(undefined);
  const [acknowledgementAudit, setAcknowledgementAudit] = useState<AuditTemp>({});
  const [acksList, setAckList] = useState<AcknowledgmentInfo[] | undefined>();

  const submitOrder = async () => {
    const request: CreateOrderRequestContent = {
      items: {
        [ItemTaxonomyId.Parse(taxonomyId!).toString()]: {
          quantity: 1,
          details: {
            softwareOrderDetails: { securityTicketID: securityTicket! },
            ...(licenseInfo !== undefined && { licenseInfo }),
          },
        },
      },
      acknowledgementAudit: _.values(acknowledgementAudit),
    };
    await submitOrderHandler(request);
  };

  /* istanbul ignore next */
  const canClickSubmmit = () => {
    if (!confirmIsChecked || isLoadingCreate) return false;

    return securityTicket != undefined && securityTicket.trim();
  };

  const handleConfirmCheck = (policies: string[], ack: boolean, input?: string) => {
    acknowledgementAudit[acknowledgePolicyName] = {
      by: userAlias!,
      on: new Date(),
      policyName: acknowledgePolicyName,
      acknowledged: ack,
    };
  };

  const selectModalBodyComponent = () => {
    if (csrfTokenIsLoading) {
      return <Spinner size={SpinnerSize.Medium} fr={undefined} />;
    }

    return (
      <Col gridGap="S400">
        <Col gridGap="S200">
          <Label htmlFor="ticket-text-area">{getString("submitUnsanctionOrder.textAreaLabel")}</Label>
          <TextArea
            id="ticket-text-area"
            placeholder={getString("submitUnsanctionOrder.textAreaPlaceHolder")}
            value={securityTicket}
            touchpoint="unsactioned-ticket-text-area"
            onChange={(e) => setSecurityTicket(e.target.value)}
            data-cy="ticket-text-area"
          />
        </Col>
      </Col>
    );
  };

  const modalBodyComponent = selectModalBodyComponent();

  // Builds the ACK list
  useEffect(() => {
    let list: AcknowledgmentInfo[] | undefined = acksList;
    if (!list) {
      list = [];
    }

    // Default policy checkbox
    list.push({
      id: "checkReadAcceptableUsePolicy",
      input: acknowledgePolicyName,
      policies: [acknowledgePolicyName],
      dataCy: "read-acceptable-use-policy-checkbox",
      text: <Text>{getString("submitUnsanctionOrder.acceptConditions")}</Text>,
    });

    setAckList(list);
  }, []);

  return (
    <ModalContent
      titleText={getString("submitUnsanctionOrder.modalTitle")}
      buttons={[
        <Button
          key="submitOrder-close"
          touchpoint="unsanctioned-cancel-button"
          variant={ButtonVariant.Tertiary}
          onClick={close}
        >
          Cancel
        </Button>,
        <Button
          touchpoint="unsanctioned-submit-button"
          key="submitOrder-submit"
          data-testid="submitOrder-submit"
          data-cy="submit-order-button"
          variant={ButtonVariant.Primary}
          disabled={!canClickSubmmit()}
          onClick={submitOrder}
        >
          {orderWorkflow.startsWith("external") && externalButtonText ? externalButtonText : "Submit"}
          {isLoadingCreate ? <Spinner size={SpinnerSize.Small} fr={undefined} /> : null}
        </Button>,
      ]}
    >
      <Col gridGap="S400" rowGap="S200">
        {modalBodyComponent}
        <AcknowledgmentsList list={acksList} handleSingleCheck={handleConfirmCheck} handleReady={setConfirmIsChecked} />
        <Col gridGap="S200" alignSelf="flex-start">
          <Text fontSize="T100" color="neutral70">
            {getString("submitUnsanctionOrder.requiredField")}
          </Text>
        </Col>
        <MessagesList messages={messages}></MessagesList>
      </Col>
    </ModalContent>
  );
};
